import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Phone, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { trackWhatsApp, trackPhoneCall } from "@/components/layout/WhatsAppButton";

const LOGO_URL = "/logo.png";

const navLinks = [
  { label: "Compre Online", href: "/loja-online", highlight: true },
  { label: "Seguros", children: [
    { label: "Seguro Auto", href: "/seguro-auto" },
    { label: "Seguro Empresarial", href: "/seguro-empresarial" },
    { label: "Seguro de Vida", href: "/seguro-de-vida" },
  ]},
  { label: "Seguradoras", href: "/seguradoras" },
  { label: "Assistência 24h", href: "/assistencia-24h" },
  { label: "Blog", href: "/blog" },
  { label: "Sobre", href: "/sobre" },
  { label: "Contato", href: "/contato" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropOpen(false); }, [location]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-sm" : "bg-transparent"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Núcleo Corretora" className="h-11 md:h-14 w-auto" />
            <div className="hidden sm:flex flex-col">
              <span className={`text-xl font-heading font-extrabold leading-none tracking-tight transition-colors ${scrolled ? "text-navy" : "text-white"}`}>NÚCLEO</span>
              <span className={`text-[9px] font-semibold tracking-[0.25em] uppercase mt-0.5 transition-colors ${scrolled ? "text-gold" : "text-gold/90"}`}>Corretora de Seguros</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
            <Link to="/" className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${scrolled ? "text-slate-700 hover:text-brand" : "text-white/90 hover:text-white"}`}>Início</Link>
            {navLinks.map((item) =>
              item.children ? (
                <div key={item.label} className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
                  <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${scrolled ? "text-slate-700 hover:text-brand" : "text-white/90 hover:text-white"}`} data-testid="nav-seguros-dropdown">
                    {item.label} <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  {dropOpen && (
                    <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border py-2 animate-fade-in">
                      {item.children.map((child) => (
                        <Link key={child.href} to={child.href} className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-brand transition-colors" data-testid={`nav-link-${child.href.slice(1)}`}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.href} to={item.href} className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${item.highlight ? (scrolled ? "text-gold font-bold" : "text-gold font-bold") : (scrolled ? "text-slate-700 hover:text-brand" : "text-white/90 hover:text-white")}`} data-testid={`nav-link-${item.href.slice(1)}`}>
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+553125550207" onClick={() => trackPhoneCall('navbar')} className={`flex items-center gap-1.5 text-sm transition-colors ${scrolled ? "text-slate-600 hover:text-brand" : "text-white/80 hover:text-white"}`}>
              <Phone className="h-3.5 w-3.5" /> (31) 2555-0207
            </a>
            <Link to="/cotacao">
              <Button className="btn-gold text-sm px-5 py-2 h-auto" data-testid="nav-cta-cotacao">Solicitar Cotação</Button>
            </Link>
          </div>

          {/* Mobile */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="nav-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 pt-10">
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
              <div className="flex flex-col gap-1">
                <Link to="/" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Início</Link>
                <Link to="/loja-online" className="px-4 py-3 text-base font-bold text-gold hover:bg-gold/5 rounded-lg">Compre Online</Link>
                <p className="px-4 pt-3 pb-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">Seguros</p>
                <Link to="/seguro-auto" className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg pl-6">Seguro Auto</Link>
                <Link to="/seguro-empresarial" className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg pl-6">Seguro Empresarial</Link>
                <Link to="/seguro-de-vida" className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg pl-6">Seguro de Vida</Link>
                <Link to="/cotacao" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Cotação Online</Link>
                <Link to="/seguradoras" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Seguradoras</Link>
                <Link to="/assistencia-24h" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Assistência 24h</Link>
                <Link to="/blog" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Blog</Link>
                <Link to="/sobre" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Sobre</Link>
                <Link to="/contato" className="px-4 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-lg">Contato</Link>
                <div className="mt-4 px-4 space-y-3">
                  <Link to="/cotacao" className="btn-gold block text-center text-sm py-3 rounded-lg" data-testid="nav-mobile-cotacao">Solicitar Cotação</Link>
                  <a href="https://wa.me/553125550207" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp('mobile_menu')} className="btn-whatsapp block text-center text-sm py-3 rounded-lg">Falar no WhatsApp</a>
                  <div className="flex justify-center gap-3 mt-2">
                    <a href="https://www.facebook.com/profile.php?id=100064945274971" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-brand hover:text-white hover:border-brand transition-all" aria-label="Facebook"><Facebook className="h-4 w-4" /></a>
                    <a href="https://www.instagram.com/nucleoseguros" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-brand hover:text-white hover:border-brand transition-all" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
